import { createAsyncThunk } from '@reduxjs/toolkit';

import dayjs, { DATE_FORMATS } from '../../utils/dateUtils';
import { SONAR_PROJECT_RANGE_TYPE, SONAR_RANGE_TYPE } from '../../constants';
import callApi from '../../service/callApi';
import sendRequest from '../../service/sendRequest';
import { cancelNavig8, setNavig8AbortController } from './sonarReducer';

const SET_CHART_VIEW = 'sonar/dashboardActions/SET_CHART_VIEW';
const SET_CHART_VIEW_GROUP = 'sonar/dashboardActions/SET_CHART_VIEW_GROUP';
const GET_SONAR_DATA = 'sonar/dashboardActions/GET_SONAR_DATA';
const GET_SONAR_COLLECT_DATA_STATUS = 'sonar/dashboardActions/GET_SONAR_COLLECT_DATA_STATUS';
const GET_SONAR_NAVIG8 = 'sonar/dashboardActions/GET_SONAR_NAVIG8';
const REPLY_SONAR_NAVIG8 = 'sonar/dashboardActions/REPLY_SONAR_NAVIG8';

export const setChartView = createAsyncThunk(SET_CHART_VIEW, async ({ section, index, view }) => ({
  section,
  index,
  view,
}));
export const setChartViewGroup = createAsyncThunk(SET_CHART_VIEW_GROUP, async ({ section, index, group }) => ({
  section,
  index,
  group,
}));

export const getSonarProjectName = async (id) => {
  const response = await sendRequest(() => callApi.get(`/api/project/project-name/${id}`));

  return response.data;
};

export const getSonarData = createAsyncThunk(GET_SONAR_DATA, async ({ id }, { getState, rejectWithValue }) => {
  const {
    [SONAR_RANGE_TYPE.COMPARABLE]: { startDate: comparableStartDate, endDate: comparableEndDate },
    [SONAR_RANGE_TYPE.COMPARE_TO]: { startDate: compareToStartDate, endDate: compareToEndDate },
  } = getState().sonarDashboard.dateRanges;

  const ranges = constructSonarRequestDates({
    comparableStartDate,
    comparableEndDate,
    compareToStartDate,
    compareToEndDate,
  });
  const serverUrl = getState().securityNew.serverUrl;

  if (ranges) {
    const response = await sendRequest(() =>
      callApi.post(`/api/calculations?projectId=${id}`, {
        ranges,
      }),
    );

    return { content: response.data, serverUrl };
  } else {
    return rejectWithValue(null);
  }
});

export const getSonarCollectDataStatus = createAsyncThunk(
  GET_SONAR_COLLECT_DATA_STATUS,
  async (id, { rejectWithValue }) => {
    const response = await sendRequest(() => callApi.get(`/api/jobs/sonar/dates/${id}`), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });
    if (response.data) {
      const data = response.data;
      return {
        collectedFrom: data.collectedFrom,
        collectedTill: data.collectedTill,
        rangeType: data.rangeType || SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE,
      };
    }
    return { collectedFrom: null, collectedTill: null, rangeType: SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE };
  },
);

export const getSonarNavig8 = createAsyncThunk(
  GET_SONAR_NAVIG8,
  async (projectId, { rejectWithValue, getState, dispatch }) => {
    const {
      [SONAR_RANGE_TYPE.COMPARABLE]: { startDate: comparableStartDate, endDate: comparableEndDate },
      [SONAR_RANGE_TYPE.COMPARE_TO]: { startDate: compareToStartDate, endDate: compareToEndDate },
    } = getState().sonarDashboard.dateRanges;

    const ranges = constructSonarRequestDates({
      comparableStartDate,
      comparableEndDate,
      compareToStartDate,
      compareToEndDate,
    });

    const controller = new AbortController();
    dispatch(setNavig8AbortController(controller));

    if (ranges) {
      const response = await sendRequest(
        () =>
          callApi.post(
            `/api/sonar/navig8?projectId=${projectId}`,
            {
              ranges,
            },
            { signal: controller.signal },
          ),
        {
          onError: (e) => {
            dispatch(cancelNavig8());
            return rejectWithValue(e);
          },
        },
      );

      dispatch(setNavig8AbortController(null));
      return response.data;
    } else {
      dispatch(cancelNavig8());
      return rejectWithValue(null);
    }
  },
);

export const replySonarNavig8 = createAsyncThunk(
  REPLY_SONAR_NAVIG8,
  async ({ reply }, { rejectWithValue, getState, dispatch }) => {
    const { thread } = getState().sonarDashboard.navig8;

    const controller = new AbortController();
    dispatch(setNavig8AbortController(controller));

    const response = await sendRequest(
      () =>
        callApi.post(
          `/api/sonar/navig8/reply`,
          {
            reply,
            threadId: thread,
          },
          { signal: controller.signal },
        ),
      {
        onError: (e) => {
          dispatch(cancelNavig8());
          return rejectWithValue(e);
        },
      },
    );

    dispatch(setNavig8AbortController(null));
    return response.data;
  },
);

export const constructSonarRequestDates = ({
  comparableStartDate,
  comparableEndDate,
  compareToStartDate,
  compareToEndDate,
}) => {
  const today = dayjs().startOf('day');
  const formattedComparableStartDate = dayjs(comparableStartDate, DATE_FORMATS.API_DATE).isSameOrAfter(today)
    ? today.subtract(1, 'days').format(DATE_FORMATS.API_DATE)
    : comparableStartDate;

  if (!comparableEndDate || !formattedComparableStartDate || !compareToEndDate || !compareToStartDate) {
    return null;
  }

  return {
    [SONAR_RANGE_TYPE.COMPARABLE]: {
      startDate: formattedComparableStartDate,
      endDate: comparableEndDate !== comparableStartDate ? comparableEndDate : null,
    },
    [SONAR_RANGE_TYPE.COMPARE_TO]: {
      startDate: compareToStartDate,
      endDate: compareToEndDate !== compareToStartDate ? compareToEndDate : null,
    },
  };
};
